// src/pages/AdminPage.tsx
import * as React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import AppTheme from '../theme/AppTheme';
import ColorModeSelect from '../theme/ColorModeSelect';
import { db } from '../firebase/firebase';
import AdminHeaderContent from '../components/AdminHeader';

interface CargoAnalysisData {
  tons: number;
  from: string;
  to: string;
  analysis: {
    mode: string;
    price: string;
    day: string;
  }[];
}

const AdminPage: React.FC = () => {
  const [searches, setSearches] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = React.useState<CargoAnalysisData | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const [billingRecords, setBillingRecords] = React.useState<any[]>([]);
  const [billingLoading, setBillingLoading] = React.useState(true);

  React.useEffect(() => {
    const q = query(collection(db, 'cargo_searches'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const searchData: any[] = [];
        querySnapshot.forEach((doc) => {
          searchData.push({ id: doc.id, ...doc.data() });
        });
        setSearches(searchData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching cargo searches: ', error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    const q = query(collection(db, 'cargo_billings'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const billData: any[] = [];
        querySnapshot.forEach((doc) => {
          billData.push({ id: doc.id, ...doc.data() });
        });
        setBillingRecords(billData);
        setBillingLoading(false);
      },
      (error) => {
        console.error('Error fetching cargo billings: ', error);
        setBillingLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const handleViewDetails = (analysis: CargoAnalysisData) => {
    setSelectedAnalysis(analysis);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAnalysis(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Component to display cargo analysis results
  const CargoAnalysisDisplay: React.FC<{ analysis: CargoAnalysisData }> = ({ analysis }) => {
   
    const validOptions = analysis.analysis.filter(option => 
      option.price && 
      option.day && 
      option.price.toLowerCase() !== 'n/a' && 
      option.day.toLowerCase() !== 'n/a'
    );

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Cargo Transport Options
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {analysis.tons} ton(s) from <strong>{analysis.from}</strong> to <strong>{analysis.to}</strong>
        </Typography>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Transport Modes: {validOptions.length} of {analysis.analysis.length}
          </Typography>
          {validOptions.length > 0 ? (
            validOptions.map((option, index) => (
              <Paper 
                key={index} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1 
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {option.mode.toUpperCase()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Cost: ${option.price}`} 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={`Delivery in ${option.day} day(s)`} 
                    color="secondary" 
                    variant="outlined" 
                  />
                </Box>
              </Paper>
            ))
          ) : (
            <Typography color="error">
              No valid transport options found for this route and cargo weight.
            </Typography>
          )}

          {/* Show unavailable options with N/A values with reduced opacity */}
          {analysis.analysis.length > validOptions.length && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                Unavailable Options:
              </Typography>
              {analysis.analysis
                .filter(option => 
                  !option.price || 
                  !option.day || 
                  option.price.toLowerCase() === 'n/a' || 
                  option.day.toLowerCase() === 'n/a'
                )
                .map((option, index) => (
                  <Paper 
                    key={`unavailable-${index}`} 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 1,
                      opacity: 0.6
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {option.mode.toUpperCase()}
                    </Typography>
                    <Typography color="error" variant="body2">
                      Not available for this cargo weight/route
                    </Typography>
                  </Paper>
                ))
              }
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 2000 }} />
      <AdminHeaderContent />
      <Container sx={{ mt: 4, mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
          <Tab label="Search History" id="tab-0" />
          <Tab label="Billing Records" id="tab-1" />
        </Tabs>

        {/* Search History Tab */}
        <Box role="tabpanel" hidden={tabValue !== 0} id="tabpanel-0" aria-labelledby="tab-0" sx={{ pt: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {searches.map((search) => {
                const timeString = search.timestamp?.toDate
                  ? search.timestamp.toDate().toLocaleString()
                  : String(search.timestamp);
                return (
                  <Grid item xs={12} sm={6} md={4} key={search.id}>
                    <Card
                      component={Paper}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 2px 5px rgba(0,0,0,0.4)'
                            : '0 2px 5px rgba(0,0,0,0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardHeader
                        title={`${search.query?.tons || 'N/A'} ton(s) cargo`}
                        subheader={
                          <>
                            <Typography variant="caption" color="text.secondary">
                              User: {search.email || "unknown"}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              From {search.query?.from || 'N/A'} to {search.query?.to || 'N/A'}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {`Searched on: ${timeString}`}
                            </Typography>
                          </>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {search.analysis && search.analysis.analysis
                            ? `${search.analysis.analysis.filter((opt: { price: string; day: string }) => 
                                opt.price && 
                                opt.day && 
                                opt.price.toLowerCase() !== 'n/a' && 
                                opt.day.toLowerCase() !== 'n/a'
                              ).length} transport options available`
                            : "No analysis available."}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                        <Button variant="contained" onClick={() => handleViewDetails(search.analysis)}>
                          View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* Billing Records Tab */}
        <Box role="tabpanel" hidden={tabValue !== 1} id="tabpanel-1" aria-labelledby="tab-1" sx={{ pt: 3 }}>
          {billingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {billingRecords.map((bill) => {
                const timeString = bill.timestamp?.toDate
                  ? bill.timestamp.toDate().toLocaleString()
                  : String(bill.timestamp);
                
                // Calculate total price
                const basePrice = parseInt(bill.cargoData.price.replace(/[^\d]/g, ''));
                const total = basePrice + Math.round(basePrice * 0.15); // 10% tax + 5% handling

                return (
                  <Grid item xs={12} sm={6} md={4} key={bill.id}>
                    <Card
                      component={Paper}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 2px 5px rgba(0,0,0,0.4)'
                            : '0 2px 5px rgba(0,0,0,0.1)',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardHeader
                        title={`${bill.cargoData.tons} ton(s) cargo`}
                        subheader={
                          <>
                            <Typography variant="caption" color="text.secondary">
                              User: {bill.email || "unknown"}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              From {bill.cargoData.from} to {bill.cargoData.to}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {`Paid on: ${timeString}`}
                            </Typography>
                          </>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Transport mode: <strong>{bill.cargoData.transportMode.toUpperCase()}</strong>
                        </Typography>
                        <Typography variant="body2">
                          Total amount: <strong>${total}</strong>
                        </Typography>
                        <Typography variant="body2">
                          Payment method: {bill.paymentMethod}
                        </Typography>
                        <Typography variant="body2">
                          Status: <Chip label={bill.paymentStatus} color="success" size="small" sx={{ mt: 0.5 }} />
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

       
        <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>
            Cargo Analysis Details
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedAnalysis ? (
              <CargoAnalysisDisplay analysis={selectedAnalysis} />
            ) : (
              <Typography>No analysis available.</Typography>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </AppTheme>
  );
};

export default AdminPage;
